// Dagrun PM domain types
// IMPORTANT: All field names are ACTUAL Dataverse logical names.
// Discovery confirmed: project module fields HAVE underscores (dagrun_pm_module_issues).
// All other multi-word fields are collapsed (dagrun_pm_estimatedhours, dagrun_pm_wipcollectionid).
// Title field on every table is dagrun_pm_name (PrimaryNameAttribute), NOT dagrun_pm_title.

export interface Task {
  dagrun_pm_taskid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_status: number;
  dagrun_pm_priority: number;
  dagrun_pm_tasktype: number;
  dagrun_pm_assigneeid: string | null;
  dagrun_pm_assigneename: string | null;
  dagrun_pm_duedate: string | null;
  dagrun_pm_startdate: string | null;
  dagrun_pm_cardcolor: number | null;
  dagrun_pm_isoncriticalpath: boolean;
  dagrun_pm_dependencyslipflag: boolean;
  dagrun_pm_dependencyslipdays: number | null;
  dagrun_pm_percentcomplete: number;
  dagrun_pm_estimatedhours: number | null;
  dagrun_pm_effortcompletedhours: number;
  dagrun_pm_commentcount: number;
  dagrun_pm_subtaskcount: number;
  dagrun_pm_subtaskcompletedcount: number;
  dagrun_pm_checklistcount: number;
  dagrun_pm_checklistcompletedcount: number;
  dagrun_pm_attachmentcount: number;
  dagrun_pm_hasdependencies: boolean;
  dagrun_pm_hasaiactivity: boolean;
  dagrun_pm_isrecurringinstance: boolean;
  dagrun_pm_occurrencenumber?: number | null;
  dagrun_pm_hasgoals?: boolean;
  dagrun_pm_goalcount?: number;
  dagrun_pm_storypoints?: number | null;
  _dagrun_pm_wipcollectionid_value: string | null;
  _dagrun_pm_parenttaskid_value: string | null;
  _dagrun_pm_projectid_value: string | null;
  _dagrun_pm_sprintid_value?: string | null;
  _dagrun_pm_phaseid_value?: string | null;
  createdon?: string;
  dagrun_pm_sortorder?: number | null;
  dagrun_pm_baselinestart?: string | null;
  dagrun_pm_baselineend?: string | null;
  // Scheduling constraint (imported from MS Project). 100000000 = None (ASAP).
  dagrun_pm_constrainttype?: number | null;
  dagrun_pm_constraintdate?: string | null;
}

export const TaskConstraintType = {
  None:               100000000,
  MustStartOn:        100000001,
  MustFinishOn:       100000002,
  StartNoEarlierThan: 100000003,
  FinishNoLaterThan:  100000004,
} as const;

export const CONSTRAINT_TYPE_LABEL: Record<number, string> = {
  [TaskConstraintType.None]:               'None (ASAP)',
  [TaskConstraintType.MustStartOn]:        'Must Start On',
  [TaskConstraintType.MustFinishOn]:       'Must Finish On',
  [TaskConstraintType.StartNoEarlierThan]: 'Start No Earlier Than',
  [TaskConstraintType.FinishNoLaterThan]:  'Finish No Later Than',
};

export interface Dependency {
  dagrun_pm_dependencyid: string;
  dagrun_pm_dependencytype: number;
  dagrun_pm_lagdays?: number | null;
  _dagrun_pm_predecessortaskid_value: string;
  _dagrun_pm_successortaskid_value: string;
  _dagrun_pm_projectid_value: string;
}

export const DependencyType = {
  FS: 100000000, // Finish-to-Start
  SS: 100000001,
  FF: 100000002,
  SF: 100000003,
} as const;

export interface Project {
  dagrun_pm_projectid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_lifecyclestatus: number;
  dagrun_pm_methodologymode: number;
  dagrun_pm_percentcomplete: number;
  dagrun_pm_healthrag: number | null;
  dagrun_pm_m365groupid: string | null;
  dagrun_pm_sharepointsiteurl?: string | null;
  dagrun_pm_teamsteamid: string | null;
  dagrun_pm_loopworkspaceid?: string | null;
  dagrun_pm_currency?: string | null;
  dagrun_pm_isbillable?: boolean;
  dagrun_pm_openissuescount?: number;
  dagrun_pm_openriskscount?: number;
  dagrun_pm_nextmilestonedate?: string | null;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername?: string | null;
  dagrun_pm_baseline_label?: string | null;
  dagrun_pm_baseline_capturedon?: string | null;
  dagrun_pm_resourcepoolid?: string | null;
  dagrun_pm_useglobalresourcepool?: boolean;
  // Module flags — actual Dataverse names HAVE underscores
  dagrun_pm_module_issues: boolean;
  dagrun_pm_module_risks: boolean;
  dagrun_pm_module_milestones: boolean;
  dagrun_pm_module_timetracking: boolean;
  dagrun_pm_module_budget: boolean;
  dagrun_pm_module_resources: boolean;
  dagrun_pm_module_changemanagement: boolean;
  dagrun_pm_module_meetingintel: boolean;
  dagrun_pm_module_clientportal: boolean;
  dagrun_pm_module_ai: boolean;
  dagrun_pm_module_agile: boolean;
  dagrun_pm_module_goals?: boolean;
  // New Apr 19 module flags (Block 3 amendments)
  dagrun_pm_module_training?: boolean;
  dagrun_pm_module_punchlist?: boolean;
  dagrun_pm_module_stakeholders?: boolean;
  dagrun_pm_module_submittalrfi?: boolean;
  dagrun_pm_module_lessons?: boolean;
}

export interface Goal {
  dagrun_pm_goalid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_goaltype: number;
  dagrun_pm_status: number;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername: string | null;
  dagrun_pm_duedate: string | null;
  dagrun_pm_targetvalue: number | null;
  dagrun_pm_currentvalue: number | null;
  dagrun_pm_unitlabel: string | null;
  dagrun_pm_sortorder: number;
  dagrun_pm_clientvisible: boolean;
  _dagrun_pm_projectid_value: string;
}

export interface GoalTask {
  dagrun_pm_goaltaskid: string;
  dagrun_pm_name: string;
  dagrun_pm_addedby?: string | null;
  dagrun_pm_addedon?: string | null;
  _dagrun_pm_goalid_value: string;
  _dagrun_pm_taskid_value: string;
  '_dagrun_pm_taskid_value@OData.Community.Display.V1.FormattedValue'?: string;
}

export const GoalType = {
  TaskCompletion: 100000000,
  Numeric:        100000001,
  Currency:       100000002,
  YesNo:          100000003,
} as const;

export const GoalStatus = {
  NotStarted: 100000000,
  InProgress: 100000001,
  AtRisk:     100000002,
  Complete:   100000003,
  Cancelled:  100000004,
} as const;

export interface Collection {
  dagrun_pm_collectionid: string;
  dagrun_pm_name: string;
  dagrun_pm_sortorder: number;
  dagrun_pm_isdonecollection: boolean;
  dagrun_pm_wiplimit: number | null;
  dagrun_pm_color: string | null;
  dagrun_pm_ishidden: boolean;
  _dagrun_pm_viewid_value: string | null;
  _dagrun_pm_projectid_value: string | null;
}

export interface Issue {
  dagrun_pm_issueid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_severity: number;
  dagrun_pm_status: number;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_resolutionnotes?: string | null;
  dagrun_pm_clientvisible: boolean;
  dagrun_pm_resolvedon?: string | null;
  createdon?: string;
  _dagrun_pm_projectid_value: string;
  // Owner is stored as text in this schema (no lookup)
  ownerName?: string | null; // client-side derived
}

export interface Risk {
  dagrun_pm_riskid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_probability: number;
  dagrun_pm_impact: number;
  dagrun_pm_priorityscore: number;           // Legacy integer score (1–9). Still populated for back-compat.
  dagrun_pm_riskscore?: number | null;       // New decimal 0.0–1.0.
  dagrun_pm_rag?: number | null;             // New picklist — 1 Green / 2 Amber / 3 Red.
  dagrun_pm_status: number;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername?: string | null;
  dagrun_pm_mitigationplan?: string | null;
  dagrun_pm_contingencyplan?: string | null;
  dagrun_pm_clientvisible: boolean;
  _dagrun_pm_projectid_value: string;
}

export const RiskRAG = { Green: 1, Amber: 2, Red: 3 } as const;

export interface Milestone {
  dagrun_pm_milestoneid: string;
  dagrun_pm_name: string;
  dagrun_pm_duedate: string | null;
  dagrun_pm_completeddate?: string | null;
  dagrun_pm_status: number;
  dagrun_pm_completioncriteria?: string | null;
  dagrun_pm_isclientvisible: boolean;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername?: string | null;
  createdon?: string;
  _dagrun_pm_projectid_value: string;
  _dagrun_pm_phaseid_value?: string | null;
}

export interface ChangeRequest {
  dagrun_pm_changerequestid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_impactassessment?: string | null;
  dagrun_pm_status: number;
  dagrun_pm_submittedby?: string | null;
  dagrun_pm_submittedbyname?: string | null;
  dagrun_pm_submittedon?: string | null;
  dagrun_pm_reviewedby?: string | null;
  dagrun_pm_reviewedbyname?: string | null;
  dagrun_pm_reviewedon?: string | null;
  dagrun_pm_approvalnotes?: string | null;
  createdon?: string;
  _dagrun_pm_projectid_value: string;
}

export interface TimeEntry {
  dagrun_pm_timeentryid: string;
  dagrun_pm_name: string;
  dagrun_pm_userid?: string | null;
  dagrun_pm_username?: string | null;
  dagrun_pm_logdate: string;
  dagrun_pm_hours: number;
  dagrun_pm_isbillable: boolean;
  dagrun_pm_notes?: string | null;
  createdon?: string;
  _dagrun_pm_projectid_value: string;
  _dagrun_pm_taskid_value?: string | null;
}

export interface Budget {
  dagrun_pm_budgetid: string;
  dagrun_pm_name: string;
  dagrun_pm_budgettype: number;
  dagrun_pm_allocatedamount: number;
  dagrun_pm_currency?: string | null;
  _dagrun_pm_projectid_value: string;
  _dagrun_pm_phaseid_value?: string | null;
}

export interface Expense {
  dagrun_pm_expenseid: string;
  dagrun_pm_name: string;
  dagrun_pm_amount: number;
  dagrun_pm_currency?: string | null;
  dagrun_pm_category: number;
  dagrun_pm_submittedby?: string | null;
  dagrun_pm_submittedbyname?: string | null;
  dagrun_pm_submittedon?: string | null;
  dagrun_pm_approvalstatus: number;
  dagrun_pm_approvedby?: string | null;
  dagrun_pm_receipturl?: string | null;
  createdon?: string;
  _dagrun_pm_projectid_value: string;
  _dagrun_pm_taskid_value?: string | null;
}

export interface Sprint {
  dagrun_pm_sprintid: string;
  dagrun_pm_name: string;
  dagrun_pm_sprintgoal?: string | null;
  dagrun_pm_startdate: string | null;
  dagrun_pm_enddate: string | null;
  dagrun_pm_status: number;
  dagrun_pm_pointsplanned: number;
  dagrun_pm_pointscompleted: number;
  _dagrun_pm_projectid_value: string;
}

export interface BurndownPoint {
  dagrun_pm_burndownid: string;
  dagrun_pm_snapshotdate: string;
  dagrun_pm_pointsremaining: number;
  dagrun_pm_idealremaining: number;
  _dagrun_pm_sprintid_value: string;
}

export interface AgentConfig {
  dagrun_pm_agentconfigid: string;
  dagrun_pm_name: string;
  dagrun_pm_autonomylevel: number;
  dagrun_pm_isenabled: boolean;
  dagrun_pm_atriskthresholdoverduetasks: number | null;
  dagrun_pm_atriskthresholdbudgetpct: number | null;
  dagrun_pm_askdagruntmscope: number;
  _dagrun_pm_projectid_value: string;
}

export interface AgentSuggestion {
  dagrun_pm_agentsuggestionid: string;
  dagrun_pm_name: string;
  dagrun_pm_suggestiontype: number;
  dagrun_pm_suggestiontext: string;
  dagrun_pm_entitytype?: string | null;
  dagrun_pm_entitytitle?: string | null;
  dagrun_pm_status: number;
  _dagrun_pm_projectid_value: string;
}

export interface AutomationRule {
  dagrun_pm_automationruleid: string;
  dagrun_pm_name: string;
  dagrun_pm_triggertype: number;
  dagrun_pm_actiontype: number;
  dagrun_pm_isenabled: boolean;
  dagrun_pm_lasttriggeredon?: string | null;
  dagrun_pm_triggercount: number;
  dagrun_pm_triggerparamsjson?: string | null;
  dagrun_pm_conditionjson?: string | null;
  dagrun_pm_actionparamsjson?: string | null;
  dagrun_pm_scope?: number | null;
  _dagrun_pm_projectid_value: string;
}

export interface PortalConfig {
  dagrun_pm_portalconfigid: string;
  dagrun_pm_name: string;
  dagrun_pm_isenabled: boolean;
  dagrun_pm_sectionsconfigjson?: string | null;
  _dagrun_pm_projectid_value: string;
}

export interface ClientProjectAccess {
  dagrun_pm_clientprojectaccessid: string;
  dagrun_pm_grantedon?: string | null;
  dagrun_pm_grantedby?: string | null;
  _dagrun_pm_contactid_value: string;
  _dagrun_pm_projectid_value: string;
  '_dagrun_pm_contactid_value@OData.Community.Display.V1.FormattedValue'?: string;
}

export interface ActivityLogEntry {
  dagrun_pm_activitylogid: string;
  dagrun_pm_name: string;
  dagrun_pm_actiontype?: string | null;
  dagrun_pm_actiondescription?: string | null;
  dagrun_pm_actorid?: string | null;
  dagrun_pm_actorname?: string | null;
  dagrun_pm_actorisai: boolean;
  dagrun_pm_entitytype?: number | null;
  dagrun_pm_entityid?: string | null;
  dagrun_pm_entitytitle?: string | null;
  createdon: string;
  _dagrun_pm_projectid_value: string;
}

export interface Baseline {
  dagrun_pm_baselineid: string;
  dagrun_pm_name: string;
  dagrun_pm_createdby?: string | null;
  dagrun_pm_createdonex?: string | null;
  dagrun_pm_notes?: string | null;
  _dagrun_pm_projectid_value: string;
}

export interface BaselineTask {
  dagrun_pm_baselinetaskid: string;
  dagrun_pm_plannedstart: string | null;
  dagrun_pm_plannedend: string | null;
  dagrun_pm_plannedurationdays: number | null;
  dagrun_pm_plannedestimatedhours: number | null;
  dagrun_pm_plannedpercentcomplete: number | null;
  dagrun_pm_plannedstatus?: string | null;
  _dagrun_pm_baselineid_value: string;
  _dagrun_pm_taskid_value: string;
}

export interface FormDef {
  dagrun_pm_formid: string;
  dagrun_pm_name: string;
  dagrun_pm_formdescription?: string | null;
  dagrun_pm_isactive: boolean;
  dagrun_pm_targetentitytype: number;
  _dagrun_pm_projectid_value?: string | null;
}

export interface Phase {
  dagrun_pm_phaseid: string;
  dagrun_pm_name: string;
  dagrun_pm_sortorder: number;
  dagrun_pm_status: number;
  _dagrun_pm_projectid_value: string;
}

export const TaskStatus = {
  NotStarted: 100000000,
  InProgress: 100000001,
  InReview:   100000002,
  Done:       100000003,
  OnHold:     100000004,
  Cancelled:  100000005,
} as const;

export const TaskPriority = {
  Critical: 100000000,
  High:     100000001,
  Medium:   100000002,
  Low:      100000003,
} as const;

export const TaskType = {
  Task:    100000000,
  Subtask: 100000001,
  Story:   100000002,
} as const;

export const ProjectLifecycleStatus = {
  Draft: 100000000, Active: 100000001, OnHold: 100000002, AtRisk: 100000003,
  Complete: 100000004, Cancelled: 100000005, Archived: 100000006,
} as const;

export const MethodologyMode = {
  Waterfall: 100000000, Agile: 100000001, Kanban: 100000002, Hybrid: 100000003,
} as const;

export const HealthRAG = { Green: 100000000, Amber: 100000001, Red: 100000002 } as const;

export const IssueSeverity = { Low: 100000000, Medium: 100000001, High: 100000002, Critical: 100000003 } as const;
export const IssueStatus = { New: 100000000, InProgress: 100000001, Resolved: 100000002, Closed: 100000003, Deferred: 100000004 } as const;

export const RiskProbability = {
  Low:      100000000,
  Medium:   100000001,
  High:     100000002,
  VeryLow:  100000003,
  VeryHigh: 100000004,
} as const;
export const RiskImpact = RiskProbability;
export const RiskStatus = { Identified: 100000000, Monitoring: 100000001, Mitigating: 100000002, Closed: 100000003, Accepted: 100000004 } as const;

export const MilestoneStatus = { NotStarted: 100000000, InProgress: 100000001, Complete: 100000002, AtRisk: 100000003, Missed: 100000004 } as const;

export const ChangeRequestStatus = {
  Submitted: 100000000, UnderReview: 100000001, Approved: 100000002, Rejected: 100000003, Withdrawn: 100000004,
} as const;

export const SprintStatus = { Planned: 100000000, Active: 100000001, Completed: 100000002, Archived: 100000003 } as const;

export const BudgetType = { Hours: 100000000, Currency: 100000001 } as const;
export const ExpenseCategory = { Travel: 100000000, Software: 100000001, Hardware: 100000002, Contractor: 100000003, Facilities: 100000004, Other: 100000005 } as const;
export const ExpenseApprovalStatus = { Pending: 100000000, Approved: 100000001, Rejected: 100000002 } as const;

export const AgentAutonomyLevel = { Advisory: 100000000, Supervised: 100000001, Autonomous: 100000002 } as const;
export const AgentScope = { ProjectWide: 100000000, PersonalOnly: 100000001 } as const;
export const AgentSuggestionStatus = { Pending: 100000000, Accepted: 100000001, Rejected: 100000002, Expired: 100000003 } as const;

export interface ChecklistItem {
  dagrun_pm_checklistitemid: string;
  dagrun_pm_name: string;
  dagrun_pm_iscompleted: boolean;
  dagrun_pm_sortorder: number | null;
  dagrun_pm_completedon?: string | null;
  dagrun_pm_completedby?: string | null;
  _dagrun_pm_taskid_value: string;
  createdon?: string;
}

export interface Comment {
  dagrun_pm_commentid: string;
  dagrun_pm_name: string;
  dagrun_pm_body: string;
  dagrun_pm_authorid: string | null;
  dagrun_pm_authorname: string | null;
  dagrun_pm_entityid: string;
  dagrun_pm_entitytype: number;
  dagrun_pm_isdeleted: boolean;
  dagrun_pm_isedited: boolean;
  dagrun_pm_isaigenerated: boolean;
  createdon: string;
}

// Comment entitytype picklist — confirmed via EntityDefinitions
export const CommentEntityType = {
  Task:           100000000,
  Issue:          100000001,
  Risk:           100000002,
  Decision:       100000003,
  ChangeRequest:  100000004,
  Milestone:      100000005,
} as const;

export type AppTheme = 'default' | 'dark' | 'contrast';

// ── Module-flag amendments on Project ─────────────────────────────────
// (These are also declared on the Project interface above; kept here as a
// developer-facing list of the new Apr 19 module flags.)
// dagrun_pm_module_training, dagrun_pm_module_punchlist,
// dagrun_pm_module_stakeholders, dagrun_pm_module_submittalrfi,
// dagrun_pm_module_lessons
//
// Since these are new schema columns, they are declared as optional booleans
// on Project via the `[key: string]` index already allowed by module access
// throughout the codebase. To get type-safe access, developers should extend
// Project with these fields explicitly as tabs are wired up.

// ── Block-3 new-table types ───────────────────────────────────────────

export interface RFP {
  dagrun_pm_rfpid: string;
  dagrun_pm_name: string;
  dagrun_pm_rfpnumber?: string | null;
  dagrun_pm_clientname?: string | null;
  dagrun_pm_duedate?: string | null;
  dagrun_pm_submissionduedate?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_side?: number | null;
  dagrun_pm_estimatedvalue?: number | null;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername?: string | null;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_notes?: string | null;
  dagrun_pm_description?: string | null;
  dagrun_pm_gonobiddecision?: string | null;
  dagrun_pm_linkedprojectid?: string | null;
  dagrun_pm_linkedprojectname?: string | null;
  dagrun_pm_crmdealid?: string | null;
  dagrun_pm_crmaccountid?: string | null;
  createdon?: string;
}

// RFP lifecycle. Values 1-5 are the legacy set kept for back-compat
// with rows created before the pipeline was modeled (Draft/Submitted/
// Won/Lost/Archived); 6-10 are the new pipeline statuses added by
// scripts/block5-rfp-schema.py. New RFPs created from the pipeline
// view use values 6+ — the legacy values render correctly when shown
// but the pipeline status control hides them from selection.
export const RFPStatus = {
  // Legacy
  Draft: 1, Submitted: 2, Won: 3, Lost: 4, Archived: 5,
  // Pipeline lifecycle
  Identified: 6, GoNoBid: 7, InPreparation: 8, UnderReview: 9, NoAward: 10,
} as const;
export type RFPStatusValue = typeof RFPStatus[keyof typeof RFPStatus];

export const RFPSide   = { Responding: 1, Issuing: 2 } as const;

export interface PunchItem {
  dagrun_pm_punchitemid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_description?: string | null;
  dagrun_pm_location?: string | null;
  dagrun_pm_trade?: string | null;
  dagrun_pm_assigneeid?: string | null;
  dagrun_pm_assigneename?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_duedate?: string | null;
  dagrun_pm_severity?: number | null;
  dagrun_pm_notes?: string | null;
  dagrun_pm_drawingreference?: string | null;
  dagrun_pm_verifiedby?: string | null;
  dagrun_pm_verifiedon?: string | null;
  dagrun_pm_itemnumber?: number | null;
  dagrun_pm_photosjson?: string | null;
  createdon?: string;
}

export const PunchStatus   = { Open: 1, InProgress: 2, ReadyForInspection: 3, Closed: 4 } as const;
export const PunchSeverity = { Minor: 1, Major: 2, Critical: 3 } as const;

// A punch item is treated as "verified" once it reaches the terminal
// Closed status — that is the inspection-passed state the verifiedby /
// verifiedon fields record. (The live prod option set is
// Open/In Progress/Ready For Inspection/Closed — see schema note in CLAUDE.)
export const PUNCH_VERIFIED_STATUS = PunchStatus.Closed;

export interface PunchPhoto {
  url: string;
  name: string;
  uploadedOn: string;
}

// One SharePoint document library linked to a project. Stores the resolved
// Graph site + drive ids so the Documents tab can browse /drives/{driveId}.
export interface ProjectLibrary {
  dagrun_pm_projectlibraryid: string;
  dagrun_pm_name: string;
  dagrun_pm_siteid?: string | null;
  dagrun_pm_driveid?: string | null;
  dagrun_pm_siteurl?: string | null;
  dagrun_pm_displayorder?: number | null;
  _dagrun_pm_projectid_value?: string | null;
}

export interface Lesson {
  dagrun_pm_lessonid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_category?: number | null;
  dagrun_pm_description?: string | null;
  dagrun_pm_impact?: string | null;
  dagrun_pm_recommendation?: string | null;
  dagrun_pm_loggedbyid?: string | null;
  dagrun_pm_loggedbyname?: string | null;
  dagrun_pm_loggedon?: string | null;
  dagrun_pm_phase?: string | null;
  dagrun_pm_tags?: string | null;
  createdon?: string;
}

export const LessonCategory = { Technical: 1, Process: 2, Communication: 3, Risk: 4, Resource: 5, Other: 6 } as const;

export interface Stakeholder {
  dagrun_pm_stakeholderid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_userid?: string | null;
  dagrun_pm_username?: string | null;
  dagrun_pm_role?: string | null;
  dagrun_pm_influence?: number | null;
  dagrun_pm_interest?: number | null;
  dagrun_pm_engagementstrategy?: string | null;
  dagrun_pm_notes?: string | null;
  createdon?: string;
}

export interface StakeholderEngagement {
  dagrun_pm_stakeholderengagementid: string;
  dagrun_pm_name: string;
  dagrun_pm_stakeholderid?: string | null;
  dagrun_pm_engagementdate?: string | null;
  dagrun_pm_type?: number | null;
  dagrun_pm_summary?: string | null;
  dagrun_pm_loggedbyid?: string | null;
  dagrun_pm_loggedbyname?: string | null;
  createdon?: string;
}

export const InfluenceLevel = { Low: 1, Medium: 2, High: 3 } as const;
export const EngagementType = { Meeting: 1, Email: 2, Call: 3, Report: 4, Other: 5 } as const;

export interface TrainingItem {
  dagrun_pm_trainingitemid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_trainingtype?: number | null;
  dagrun_pm_assigneeid?: string | null;
  dagrun_pm_assigneename?: string | null;
  dagrun_pm_duedate?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_notes?: string | null;
  createdon?: string;
}

export const TrainingType   = { Onboarding: 1, Safety: 2, Technical: 3, Compliance: 4, Other: 5 } as const;
export const TrainingStatus = { NotStarted: 1, InProgress: 2, Completed: 3, Overdue: 4 } as const;

export interface TrainingCompletion {
  dagrun_pm_trainingcompletionid: string;
  dagrun_pm_name: string;
  dagrun_pm_trainingitemid?: string | null;
  dagrun_pm_completedon?: string | null;
  dagrun_pm_completedbyid?: string | null;
  dagrun_pm_completedbyname?: string | null;
  dagrun_pm_score?: number | null;
  dagrun_pm_notes?: string | null;
  createdon?: string;
}

export interface Submittal {
  dagrun_pm_submittalid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_submittalnumber?: string | null;
  dagrun_pm_description?: string | null;
  dagrun_pm_spec?: string | null;
  dagrun_pm_submittedbyid?: string | null;
  dagrun_pm_submittedbyname?: string | null;
  dagrun_pm_submitteddate?: string | null;
  dagrun_pm_requireddate?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_reviewerid?: string | null;
  dagrun_pm_reviewername?: string | null;
  dagrun_pm_revieweddate?: string | null;
  dagrun_pm_notes?: string | null;
  createdon?: string;
}

export const SubmittalStatus = {
  Draft: 1, Submitted: 2, InReview: 3, Approved: 4,
  ApprovedAsNoted: 5, Rejected: 6, Resubmit: 7,
} as const;

export interface RFI {
  dagrun_pm_rfiid: string;
  dagrun_pm_name: string;
  dagrun_pm_projectid?: string | null;
  dagrun_pm_rfinumber?: string | null;
  dagrun_pm_question?: string | null;
  dagrun_pm_submittedbyid?: string | null;
  dagrun_pm_submittedbyname?: string | null;
  dagrun_pm_submitteddate?: string | null;
  dagrun_pm_duedate?: string | null;
  dagrun_pm_status?: number | null;
  dagrun_pm_assignedtoid?: string | null;
  dagrun_pm_assignedtoname?: string | null;
  dagrun_pm_answer?: string | null;
  dagrun_pm_answereddate?: string | null;
  dagrun_pm_priority?: number | null;
  createdon?: string;
}

export const RFIStatus   = { Open: 1, InReview: 2, Answered: 3, Closed: 4 } as const;
export const RFIPriority = { Low: 1, Medium: 2, High: 3, Critical: 4 } as const;
